import { useCallback, useState } from 'react';
import { validateAllPrompts, formatValidationErrors, promptErrors, requiredPlaceholdersOf } from '../utils/promptValidation';

const withoutKey = (object, key) => {
  const copy = { ...object };
  delete copy[key];
  return copy;
};

const fallbackPromptText = (promptKey) =>
  `[Default prompt for ${promptKey}]\n\nThis prompt will use the system default.`;

/**
 * Which editable prompts the user turned on, their edited text and its
 * placeholder errors. A prompt starts from its default text when turned on.
 */
export function useCustomPrompts(editablePrompts, defaultPrompts) {
  const [customPrompts, setCustomPrompts] = useState({});
  const [enabledPrompts, setEnabledPrompts] = useState({});
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const setPromptErrors = (promptKey, errors) => {
    setValidationErrors((prev) => (errors.length > 0 ? { ...prev, [promptKey]: errors } : withoutKey(prev, promptKey)));
  };

  const setPromptText = (promptKey, text) => {
    setCustomPrompts((prev) => ({ ...prev, [promptKey]: text }));
    const promptConfig = editablePrompts.find((p) => p.key === promptKey);
    if (promptConfig) setPromptErrors(promptKey, promptErrors(text, requiredPlaceholdersOf(promptConfig)));
  };

  const toggleEnabled = (promptKey) => {
    const isEnabled = enabledPrompts[promptKey] || false;
    setEnabledPrompts((prev) => ({ ...prev, [promptKey]: !isEnabled }));

    if (isEnabled) {
      setCustomPrompts((prev) => withoutKey(prev, promptKey));
      setPromptErrors(promptKey, []);
    } else if (!customPrompts[promptKey] && defaultPrompts[promptKey]) {
      setPromptText(promptKey, defaultPrompts[promptKey]);
    }
  };

  const resetAll = useCallback((saved) => {
    const prompts = saved && typeof saved === 'object' ? { ...saved } : {};
    setCustomPrompts(prompts);
    setEnabledPrompts(Object.fromEntries(Object.keys(prompts).map((key) => [key, true])));
    setExpandedPrompts({});
    setValidationErrors({});
  }, []);

  const cardState = (key) => ({
    promptText: customPrompts[key] || defaultPrompts[key] || fallbackPromptText(key),
    isEnabled: enabledPrompts[key] || false,
    isExpanded: expandedPrompts[key] || false,
    errors: validationErrors[key] || [],
    canReset: Boolean(defaultPrompts[key]),
    onToggleExpanded: () => setExpandedPrompts((prev) => ({ ...prev, [key]: !prev[key] })),
    onToggleEnabled: () => toggleEnabled(key),
    onChange: (text) => setPromptText(key, text),
    onReset: () => {
      if (defaultPrompts[key]) setPromptText(key, defaultPrompts[key]);
    },
  });

  /** The enabled, non-empty prompts, or an error message listing what to fix. */
  const collect = () => {
    const prompts = Object.fromEntries(
      Object.keys(enabledPrompts)
        .filter((key) => enabledPrompts[key] && customPrompts[key])
        .map((key) => [key, customPrompts[key]]),
    );
    const validation = validateAllPrompts(prompts, editablePrompts);
    if (validation.isValid) return { prompts };
    setValidationErrors(validation.errors);
    return { error: `Please fix the following errors:\n${formatValidationErrors(validation.errors, editablePrompts)}` };
  };

  return {
    cardState,
    resetAll,
    collect,
    hasErrors: Object.keys(validationErrors).length > 0,
  };
}
